import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Add, Delete, DragIndicator } from "@material-ui/icons"
import { useTheme } from "@material-ui/core/styles"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { Autocomplete } from "@material-ui/lab"
import { generateFields, getOrderedFieldsToRender, isObjectEmpty, useAutoCompleteStyles } from "../../../../../utils"
import config from "../../../../../config"
import { Button, Collapse, FormControl, TextField } from "@material-ui/core"
import CustomFormRowTitle from "./CustomRow/CustomRowTitle"
import { renderField } from "./RenderField"
import Spinner from "../../../../Spinner"
import DraggableRow from "./DraggableRow"



export default function SectionWithLists({ formik, section, catType }) {

    const { t, i18n: { language: i18nLanguage } } = useTranslation()
    const { theme: { bgColors, colors } } = useTheme()
    const [showMore, setShowMore] = useState(false)
    const [collapse, setCollapse] = useState(false)
    const [selectedField, setSelectedField] = useState("")
    const [customListsToAdd, setCustomListsToAdd] = useState([])
    const [fieldsToShow, setFieldsToShow] = useState({})
    const [sortedKeys, setSortedKeys] = useState([])
    const themeState = useSelector(state => state.userData.theme || localStorage.getItem('theme') || config.THEMES_MODES.LIGHT)
    const isContrast = themeState !== config.THEMES_MODES.LIGHT
    const classes = useAutoCompleteStyles(bgColors, colors)()

    const categories = useSelector((state) => state.adminData.sett_categories || {})
    const termCategories = useSelector(state => state.userData.termCategories)
    const customLists = !isObjectEmpty(termCategories)
        ? Object.entries(termCategories)
            .map(([key, value]) =>
                catType.includes(value.category_type) ? { id: key, name: value.name, cat_type: value.category_type } : null
            )
            .filter(Boolean)
        : []

    const currentSectFields = config.listSectionFields.find(val => val.name === section)

    useEffect(() => {
        const generatedFieldsAll = generateFields(categories[section], formik, t, termCategories, section)[currentSectFields.field]
        const filteredGeneratedFields = generatedFieldsAll && generatedFieldsAll.filter(val => val.name.startsWith(`${currentSectFields.listInitial}_`))
        const fieldsByAllowdedListId = filteredGeneratedFields && filteredGeneratedFields.reduce((acc, val) => {
            const id = val.name.split("_")[2]
            if (!acc[id]) {
                acc[id] = []
            }
            acc[id].push({
                ...val, label: val.name.split("_")[3]
            })
            return acc
        }, {})
        const customListsToAdd = customLists.filter(val => !Object.keys(fieldsByAllowdedListId).includes(val.id))
        setCustomListsToAdd(customListsToAdd)
        setFieldsToShow(fieldsByAllowdedListId)
    }, [JSON.stringify(formik.values?.settings), JSON.stringify(termCategories)])

    useEffect(() => {
        if (fieldsToShow) {
            const sorted = Object.keys(fieldsToShow).sort((a, b) => {
                const aSort = formik.values[`${currentSectFields.listInitial}_${a}_sort`] || 0
                const bSort = formik.values[`${currentSectFields.listInitial}_${b}_sort`] || 0
                return aSort - bSort
            })
            setSortedKeys(sorted)
        }
    }, [fieldsToShow])

    const moveRow = (fromIndex, toIndex) => {
        const updatedKeys = [...sortedKeys]
        const [movedItem] = updatedKeys.splice(fromIndex, 1)
        updatedKeys.splice(toIndex, 0, movedItem)
        setSortedKeys(updatedKeys)
        formik.setFormikState((prevState) => {
            const newValues = { ...prevState.values }
            updatedKeys.forEach((id, idx) => {
                const fieldName = `${currentSectFields.listInitial}_${id}_sort`
                if (newValues[fieldName] !== idx) {
                    newValues[fieldName] = idx
                }
            })
            return { ...prevState, values: newValues }
        })
    }

    const handleAllFields = (e, selectedAL, action) => {
        e.preventDefault()
        if (!selectedAL) return
        if (action === "add") {
            const fields = config.listNestedFields.map((field) => {
                let defaultValue = ""
                let noLabel = false
                switch (field.name) {
                    case "enabled":
                    case "ignoreCaseFirst":
                    case "checkSynonym":
                        defaultValue = false
                        noLabel = true
                        break
                    case "relations":
                        defaultValue = []
                        break
                    default:
                        defaultValue = 0
                }
                return {
                    id: field.id,
                    control: field.control,
                    type: field.type || "text",
                    name: `${currentSectFields.listInitial}_${selectedAL.id}_${field.name}`,
                    label: field.name,
                    defaultValue,
                    noLabel
                }
            })

            setFieldsToShow((prev) => ({
                ...prev,
                [selectedAL.id]: [...(prev[selectedAL.id] || []), ...fields],
            }))

            setCustomListsToAdd(customListsToAdd.filter((val) => val.id !== selectedAL.id))

            formik.setFormikState((prevState) => {
                const newValues = { ...prevState.values }
                fields.forEach((field) => {
                    if (!(field.name in newValues)) {
                        newValues[field.name] = field.defaultValue
                    }
                })
                return { ...prevState, values: newValues }
            })

            setSelectedField("")
        } else if (action === "remove") {
            setFieldsToShow((prev) =>
                Object.keys(prev).reduce((acc, key) => {
                    if (key === selectedAL.id) return acc
                    acc[key] = prev[key]
                    return acc
                }, {})
            )

            setCustomListsToAdd((prev) => [...prev, selectedAL].sort((a, b) => a.id - b.id))

            formik.setFormikState((prevState) => {
                const newValues = { ...prevState.values };
                (fieldsToShow[selectedAL.id] || []).forEach((field) => {
                    delete newValues[field.name]
                })
                return { ...prevState, values: newValues }
            })
        }
    }

    const handleSeeButton = (e) => {
        e.preventDefault()
        setShowMore(!showMore)
    }

    if (isObjectEmpty(termCategories)) {
        return <Spinner />
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div style={{ backgroundColor: bgColors.bgPrimary0, marginTop: collapse ? 20 : 5 }}>
                <CustomFormRowTitle text={t(section)} collapse={collapse} setCollapse={setCollapse} showMore={showMore} handleSeeButton={handleSeeButton} />
                <Collapse in={collapse} className="mt-1">
                    <div className="mb-3">
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped" style={{ width: "100%" }}>
                                {fieldsToShow && Object.keys(fieldsToShow).length > 0 &&
                                    <thead className="table-dark">
                                        <tr>
                                            {config.textTypeTableHeaders.toSpliced(1, 0, "type").map((header, index) => (
                                                <th key={index + 1} className="text-center" style={{ fontSize: 12 }}>
                                                    {t(header)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                }
                                <tbody>
                                    {
                                        sortedKeys.map((key, index) => {
                                            const alFound = customLists.find(val => val.id === key)
                                            const alName = alFound?.name[i18nLanguage] || key
                                            const alType = alFound?.cat_type || ""

                                            return (
                                                <DraggableRow key={key} id={key} index={index} moveRow={moveRow}>
                                                    <td className="font-weight-bold text-center" style={{ fontSize: 12, width: "9%", cursor:'grab' }} >
                                                        <div className="d-flex align-items-center justify-content-between" style={{ color: colors.colorText }}>
                                                            <DragIndicator style={{ color: colors.colorBlue1000 }} /> {t(alName)}
                                                        </div>
                                                    </td>
                                                    <td className="font-weight-bold text-center" style={{ fontSize: 12, width: "6%", color: colors.colorText }}>{t(alType)}</td>
                                                    <td style={{ width: "5%" }}>
                                                        {fieldsToShow[key].filter((field) => field.label === "enabled").map((field) => renderField(field, formik, t, !alFound, colors))}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex flex-wrap">
                                                            {getOrderedFieldsToRender(fieldsToShow[key], showMore, config.fieldSequence, (field) => renderField(field, formik, t, !alFound, colors))}
                                                        </div>
                                                    </td>
                                                    <td className="text-center" style={{ width: "10%" }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            disabled={!alFound}
                                                            style={{
                                                                padding: "4px",
                                                                minWidth: "30px",
                                                                backgroundColor: bgColors.bgDeleteBtn,
                                                                color: colors.colorPrimary0
                                                            }}
                                                            onClick={(e) => handleAllFields(e, customLists.find(val => val.id === key), "remove")}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </Button>
                                                    </td>
                                                </DraggableRow>
                                            )
                                        })
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div> 
                    <div className="d-flex align-items-center mb-3 ml-1">
                        <FormControl style={{ width: "20%" }}>
                            <Autocomplete
                                options={customListsToAdd}
                                getOptionLabel={(option) => option?.name?.[i18nLanguage] || ""}
                                value={customListsToAdd.find(field => field.id === selectedField) || null}
                                onChange={(e, newValue) => {
                                    setSelectedField(newValue?.id || "")
                                }}
                                style={{ backgroundColor: bgColors.bgPrimary200, borderRadius: '4px' }}
                                classes={{
                                    paper: isContrast ? classes.paper : '',
                                    option: isContrast ? classes.option : '',
                                    clearIndicator: isContrast ? classes.clearIndicator : '',
                                    popupIndicator: isContrast ? classes.popupIndicator : '',
                                    noOptions: isContrast ? classes.noOptions : '',
                                    inputRoot: isContrast ? classes.inputRoot : ''
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{ ...params.InputProps, style: { color: colors.colorText } }}
                                        InputLabelProps={{ style: { color: colors.colorText } }}
                                        variant="outlined"
                                        size="small"
                                        label={t('Add a Term List')}
                                    />
                                )}
                            />
                        </FormControl>
                        {customListsToAdd?.length > 0 && <Button variant={"contained"} color={"primary"} style={{ backgroundColor: bgColors.bgBlue1000, color: colors.colorPrimary0 }} className="ml-2"
                            onClick={(e) => handleAllFields(e, customListsToAdd.find(val => val.id === selectedField), "add")}
                        ><Add /> </Button>
                        }
                    </div>
                </Collapse>
            </div>
        </DndProvider>
    )
}